if (!window.__bundleGrindSelectorsInitialized) {
  window.__bundleGrindSelectorsInitialized = true;

  const API_VERSION_FALLBACK = '2025-10';
  const TOKEN_PLACEHOLDER = 'PASTE_YOUR_STOREFRONT_TOKEN_HERE';
  const ROOT_SELECTOR = '.bundle-grind-root';
  const COMPONENT_LIMIT = 10;

  /** @type {Map<string, any>} Cache GraphQL results per variant ID */
  const _bundleCache = new Map();

  function normalizeText(value) {
    return (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  function isCoffeeProduct(product) {
    if (product?.grindMeta?.value) return true;

    // El tipo puede estar en castellano o en inglés («Café de especialidad», «Specialty Coffee»)
    const type = normalizeText(product?.productType);
    return type.includes('cafe') || type.includes('coffee');
  }

  function parseOptions(value) {
    return (value || 'Whole Bean, Espresso, Filter, French Press')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function getVariantGid(numericId) {
    return `gid://shopify/ProductVariant/${numericId}`;
  }

  async function fetchBundleComponents({ variantId, token, apiVersion }) {
    const cacheKey = `${variantId}`;
    if (_bundleCache.has(cacheKey)) {
      return _bundleCache.get(cacheKey);
    }

    const response = await fetch(`/api/${apiVersion || API_VERSION_FALLBACK}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: `
          query BundleComponents($id: ID!) {
            node(id: $id) {
              ... on ProductVariant {
                id
                requiresComponents
                components(first: ${COMPONENT_LIMIT}) {
                  nodes {
                    quantity
                    productVariant {
                      id
                      title
                      product {
                        title
                        handle
                        productType
                        grindMeta: metafield(namespace: "shopify", key: "grind-size") {
                          value
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          id: getVariantGid(variantId),
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors?.length) {
      throw new Error(data.errors.map((error) => error.message).join('; '));
    }

    const result = data?.data?.node || null;
    _bundleCache.set(cacheKey, result);
    return result;
  }

  function getExistingSelections(form) {
    const selections = new Map();

    form?.querySelectorAll('select[name^="properties["]').forEach((select) => {
      if (select.name && select.value) {
        selections.set(select.name, select.value);
      }
    });

    return selections;
  }

  /**
   * Una entrada por bolsa: un pack con 2 Caliu da «Caliu (1/2)» y «Caliu (2/2)», para
   * poder pedir uno para moka y otro en grano. Con una sola bolsa, el nombre del café
   * a secas, como siempre (el Brain casa «Molienda · Caliu» con la línea del pedido).
   */
  function expandBags(components) {
    return components.flatMap((component) => {
      const title = component.productVariant.product.title;
      const qty = Math.max(1, Number(component.quantity || 1));
      if (qty === 1) return [{ title }];
      return Array.from({ length: qty }, (_, i) => ({ title: `${title} (${i + 1}/${qty})` }));
    });
  }

  function buildPropertyName(baseLabel, bag) {
    return `${baseLabel} · ${bag.title}`;
  }

  function renderBundleSelectors(root, components) {
    const form = root.closest('form');
    const grindLabel = root.dataset.grindLabel || 'Grind size';
    const propertyLabel = root.dataset.grindPropertyLabel || 'Grind Size';
    const options = parseOptions(root.dataset.grindOptions);
    const required = root.dataset.grindRequired === 'true';
    const previousSelections = getExistingSelections(form);
    const bags = expandBags(components);

    const wrapper = document.createElement('div');
    wrapper.className = 'bundle-grind-fields';

    const title = document.createElement('p');
    title.className = 'grind-field__label';
    title.textContent = grindLabel;
    wrapper.appendChild(title);

    // Un pack con varios cafés muestra un solo selector y los de cada café
    // quedan plegados (siguen siendo los que viajan en el formulario).
    const masterLabel = bags.length > 1 ? root.dataset.grindMasterLabel : '';
    const each = document.createElement('div');
    each.className = 'bundle-grind-fields__each';

    if (bags.length > 1) {
      const help = document.createElement('p');
      help.className = 'bundle-grind-fields__help';
      // Del ajuste del bloque: clavado aquí no lo alcanzaba Translate & Adapt,
      // así que en la tienda en inglés y en catalán salía en español.
      help.textContent = root.dataset.grindMultiHelp
        || 'Puedes elegir una molienda distinta para cada café del pack.';
      wrapper.appendChild(help);
    }

    bags.forEach((bag, index) => {
      const field = document.createElement('div');
      field.className = 'bundle-grind-field';

      const selectId = `${root.id}-component-${index}`;
      const propertyName = buildPropertyName(propertyLabel, bag);
      const savedValue = previousSelections.get(`properties[${propertyName}]`);

      const label = document.createElement('label');
      label.className = 'bundle-grind-field__product';
      label.setAttribute('for', selectId);
      label.textContent = bag.title;

      const select = document.createElement('select');
      select.id = selectId;
      select.className = 'grind-select';
      select.name = `properties[${propertyName}]`;

      if (required) {
        select.required = true;
      }

      options.forEach((optionValue) => {
        const option = document.createElement('option');
        option.value = optionValue;
        option.textContent = optionValue;

        if (savedValue && savedValue === optionValue) {
          option.selected = true;
        }

        select.appendChild(option);
      });

      field.appendChild(label);
      field.appendChild(select);
      (masterLabel ? each : wrapper).appendChild(field);
    });

    root.classList.toggle('bundle-grind-root--card', Boolean(masterLabel));

    if (masterLabel) {
      const selects = Array.from(each.querySelectorAll('select'));
      const same = selects.every((sel) => sel.value === selects[0].value);

      const masterField = document.createElement('div');
      masterField.className = 'bundle-grind-field';
      const masterId = `${root.id}-master`;
      const masterText = document.createElement('label');
      masterText.className = 'bundle-grind-field__product';
      masterText.setAttribute('for', masterId);
      masterText.textContent = masterLabel;
      const master = document.createElement('select');
      master.id = masterId;
      master.className = 'grind-select';
      options.forEach((optionValue) => {
        const option = document.createElement('option');
        option.value = optionValue;
        option.textContent = optionValue;
        master.appendChild(option);
      });
      master.value = selects[0].value;
      master.addEventListener('change', () => {
        selects.forEach((sel) => {
          sel.value = master.value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      masterField.appendChild(masterText);
      masterField.appendChild(master);

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'bundle-grind-fields__toggle';
      const more = root.dataset.grindMasterMore;
      const less = root.dataset.grindMasterLess;
      const setOpen = (open) => {
        each.hidden = !open;
        masterField.hidden = open;
        toggle.textContent = open ? less : more;
        toggle.setAttribute('aria-expanded', String(open));
      };
      toggle.addEventListener('click', () => {
        const opening = each.hidden;
        if (!opening) {
          // Al volver a uno solo, todos toman el valor del primero.
          master.value = selects[0].value;
          master.dispatchEvent(new Event('change'));
        }
        setOpen(opening);
      });

      wrapper.appendChild(masterField);
      wrapper.appendChild(each);
      wrapper.appendChild(toggle);
      setOpen(!same);
      const help = wrapper.querySelector('.bundle-grind-fields__help');
      if (help) help.remove();
    }

    root.replaceChildren(wrapper);
    root.hidden = false;

    if (form) attachSubmitSync(form);
  }

  /* ------------------------------------------------------------------ */
  /*  Sync grind selections to cart note_attributes so they appear      */
  /*  in the Shopify admin order even for bundle component line items.  */
  /* ------------------------------------------------------------------ */
  function collectBundleGrinds(form) {
    const attrs = {};
    form.querySelectorAll('.bundle-grind-field select').forEach((sel) => {
      const match = sel.name.match(/^properties\[(.+)\]$/);
      if (match && sel.value) {
        attrs[match[1]] = sel.value;
      }
    });
    return attrs;
  }

  async function pushNoteAttributes(attrs) {
    if (!Object.keys(attrs).length) return;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      });
    } catch (e) {
      console.warn('[bundle-grind] note_attributes sync failed', e);
    }
  }

  function attachSubmitSync(form) {
    if (form.__bundleGrindSyncAttached) return;
    form.__bundleGrindSyncAttached = true;

    form.addEventListener('submit', () => {
      const attrs = collectBundleGrinds(form);
      pushNoteAttributes(attrs);
    });
  }

  function hideBundleSelectors(root) {
    root.replaceChildren();
    root.hidden = true;
  }

  function toggleSingleSelector(root, shouldShow) {
    const form = root.closest('form');
    const singleField = form?.querySelector('[data-single-grind-field]');
    if (!singleField) return;

    singleField.hidden = !shouldShow;

    singleField.querySelectorAll('select, input, textarea, button').forEach((el) => {
      el.disabled = !shouldShow;
    });
  }

  async function updateRoot(root, variantId) {
    const token = root.dataset.storefrontToken;
    const apiVersion = root.dataset.storefrontApiVersion || API_VERSION_FALLBACK;

    if (!token || token === TOKEN_PLACEHOLDER) {
      hideBundleSelectors(root);
      toggleSingleSelector(root, true);
      return;
    }

    if (!variantId) {
      hideBundleSelectors(root);
      toggleSingleSelector(root, true);
      return;
    }

    try {
      const bundleVariant = await fetchBundleComponents({ variantId, token, apiVersion });
      const components = bundleVariant?.components?.nodes || [];
      const coffeeComponents = bundleVariant?.requiresComponents
        ? components.filter((component) => isCoffeeProduct(component?.productVariant?.product))
        : [];

      if (coffeeComponents.length > 0) {
        renderBundleSelectors(root, coffeeComponents);
        toggleSingleSelector(root, false);
      } else {
        hideBundleSelectors(root);
        toggleSingleSelector(root, true);
      }
    } catch (error) {
      console.error('[bundle-grind-selectors]', error);
      hideBundleSelectors(root);
      toggleSingleSelector(root, true);
    }
  }

  function initRoot(root) {
    if (!(root instanceof HTMLElement)) return;

    const form = root.closest('form');
    if (!form) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return;

    if (root.dataset.bundleGrindReady !== 'true') {
      root.dataset.bundleGrindReady = 'true';

      variantInput.addEventListener('change', () => {
        updateRoot(root, variantInput.value);
      });
    }

    updateRoot(root, variantInput.value);
  }

  function refreshRoots(scope = document) {
    const roots = [];

    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => initRoot(root));
  }

  window.refreshBundleGrindSelectors = refreshRoots;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refreshRoots());
  } else {
    refreshRoots();
  }

  document.addEventListener('variant:update', (event) => {
    const target = event.target;
    const scope =
      target instanceof HTMLElement
        ? target.closest('.shopify-section, dialog, product-card') || document
        : document;

    refreshRoots(scope);
  });

  document.addEventListener('bundle-grind:refresh', (event) => {
    const container = event.detail?.container;

    if (container instanceof Element) {
      refreshRoots(container);
    }
  });

  /* ------------------------------------------------------------------ */
  /*  MutationObserver — auto-init any .bundle-grind-root that appears  */
  /*  in the DOM (handles morph, quick-add, AJAX section rendering).    */
  /* ------------------------------------------------------------------ */
  (function observeDOM() {
    if (typeof MutationObserver === 'undefined') return;

    let pending = false;

    function scan() {
      pending = false;
      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        if (root instanceof HTMLElement && root.dataset.bundleGrindReady !== 'true') {
          initRoot(root);
        }
      });
    }

    const observer = new MutationObserver(() => {
      /* Debounce: batch rapid mutations into a single scan pass */
      if (!pending) {
        pending = true;
        requestAnimationFrame(scan);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  })();
}